import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Icon } from '../../atoms/Icon';

export interface TimelineStep {
  id: string;
  label: string;
  status: 'completed' | 'active' | 'pending';
  date?: string;
  icon?: string;
}

export interface TimelineProps {
  steps: TimelineStep[];
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
}

const TimelineContainer = styled.div`
  width: 100%;
`;

const HorizontalTimeline = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.spacing[3]};
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.gray100};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray300};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    
    &:hover {
      background: ${({ theme }) => theme.colors.gray400};
    }
  }
`;

const VerticalTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const TimelineStep = styled(motion.div)<{ $status: 'completed' | 'active' | 'pending'; $size: 'sm' | 'md' | 'lg' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme, $size }) => {
    if ($size === 'sm') return theme.spacing[1];
    if ($size === 'lg') return theme.spacing[3];
    return theme.spacing[2];
  }};
  padding: ${({ theme, $size }) => {
    if ($size === 'sm') return `${theme.spacing[2]} ${theme.spacing[3]}`;
    if ($size === 'lg') return `${theme.spacing[5]} ${theme.spacing[6]}`;
    return `${theme.spacing[4]} ${theme.spacing[4]}`;
  }};
  border: 2px solid
    ${({ $status, theme }) => {
      if ($status === 'active') return theme.colors.primary;
      if ($status === 'completed') return theme.colors.success;
      return theme.colors.gray200;
    }};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  min-width: ${({ $size }) => {
    if ($size === 'sm') return '100px';
    if ($size === 'lg') return '160px';
    return '140px';
  }};
  background: ${({ $status, theme }) => {
    if ($status === 'active') return `${theme.colors.primary}10`;
    if ($status === 'completed') return `${theme.colors.success}10`;
    return theme.colors.surface;
  }};
  text-align: center;
  transition: all 0.3s ease;
  cursor: default;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const StepIcon = styled.div<{ $status: 'completed' | 'active' | 'pending' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $status, theme }) => {
    if ($status === 'active') return theme.colors.primary;
    if ($status === 'completed') return theme.colors.success;
    return theme.colors.gray300;
  }};
  color: ${({ theme }) => theme.colors.surface};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const StepLabel = styled.div<{ $size: 'sm' | 'md' | 'lg' }>`
  font-size: ${({ theme, $size }) => {
    if ($size === 'sm') return theme.typography.fontSize.xs;
    if ($size === 'lg') return theme.typography.fontSize.md;
    return theme.typography.fontSize.sm;
  }};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StepDate = styled.div<{ $size: 'sm' | 'md' | 'lg' }>`
  font-size: ${({ theme, $size }) => {
    if ($size === 'sm') return '0.625rem';
    return theme.typography.fontSize.xs;
  }};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.regular};
`;

const StepStatus = styled.div<{ $status: 'completed' | 'active' | 'pending'; $size: 'sm' | 'md' | 'lg' }>`
  font-size: ${({ theme, $size }) => {
    if ($size === 'sm') return '0.625rem';
    return theme.typography.fontSize.xs;
  }};
  color: ${({ $status, theme }) => {
    if ($status === 'completed') return theme.colors.success;
    if ($status === 'active') return theme.colors.primary;
    return theme.colors.textSecondary;
  }};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: capitalize;
`;

export const Timeline: React.FC<TimelineProps> = ({ 
  steps, 
  orientation = 'horizontal',
  size = 'md'
}) => {
  const Container = orientation === 'horizontal' ? HorizontalTimeline : VerticalTimeline;
  
  return (
    <TimelineContainer>
      <Container>
        {steps.map((step, index) => (
          <TimelineStep
            key={step.id}
            $status={step.status}
            $size={size}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StepIcon $status={step.status}>
              {step.status === 'completed' ? (
                <Icon name="CheckCircle" size={16} />
              ) : step.status === 'active' ? (
                <Icon name="Clock" size={16} />
              ) : (
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'currentColor' }} />
              )}
            </StepIcon>
            
            <StepLabel $size={size}>{step.label}</StepLabel>
            
            {step.date && (
              <StepDate $size={size}>{step.date}</StepDate>
            )}
            
            <StepStatus $status={step.status} $size={size}>
              {step.status}
            </StepStatus>
          </TimelineStep>
        ))}
      </Container>
    </TimelineContainer>
  );
};
